import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Spinner from 'components/common/Spinner';
import { getUserAccounts, UserAccountsResponse } from 'services/admin/accountsAnalysisService';
import { useTheme } from 'contexts/ThemeContext';
import {
  Globe,
  X,
  AlertCircle,
  Inbox,
  Lock,
} from 'lucide-react';

interface UserAccountsModalProps {
  userId: string;
  onClose: () => void;
}

const UserAccountsModal: React.FC<UserAccountsModalProps> = ({ userId, onClose }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<UserAccountsResponse | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Theme tokens
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const cardBg = isDark ? '#1D293D' : '#F8FAFC';
  const cardBorder = isDark ? '#314158' : '#E2E8F0';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const textPrimary = isDark ? '#E2E8F0' : '#1E293B';
  const textSecondary = isDark ? '#90A1B9' : '#64748B';
  const borderColor = isDark ? '#1D293D' : '#F1F5F9';
  const headerBg = isDark ? '#1D293D' : '#F8FAFC';
  const iconContainerBg = isDark ? '#1C398E' : '#EFF6FF';
  const iconColor = isDark ? '#51A2FF' : '#2B7FFF';
  const inputBg = isDark ? '#1D293D' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#D1D5DB';
  const inputText = isDark ? '#E2E8F0' : '#374151';

  // Stat card color sets
  const statCardColors = {
    blue: {
      bg: isDark ? '#1C398E' : '#EFF6FF',
      border: isDark ? '#2B5BCC' : '#BFDBFE',
      value: isDark ? '#51A2FF' : '#2563EB',
      label: isDark ? '#7BBDFF' : '#1D4ED8',
    },
    green: {
      bg: isDark ? '#14532D' : '#F0FDF4',
      border: isDark ? '#166534' : '#BBF7D0',
      value: isDark ? '#4ADE80' : '#16A34A',
      label: isDark ? '#86EFAC' : '#15803D',
    },
    purple: {
      bg: isDark ? '#581C87' : '#FAF5FF',
      border: isDark ? '#6B21A8' : '#E9D5FF',
      value: isDark ? '#C084FC' : '#9333EA',
      label: isDark ? '#D8B4FE' : '#7E22CE',
    },
    teal: {
      bg: isDark ? '#134E4A' : '#F0FDFA',
      border: isDark ? '#115E59' : '#99F6E4',
      value: isDark ? '#2DD4BF' : '#0D9488',
      label: isDark ? '#5EEAD4' : '#0F766E',
    },
    amber: {
      bg: isDark ? '#78350F' : '#FFFBEB',
      border: isDark ? '#92400E' : '#FDE68A',
      value: isDark ? '#FBBF24' : '#D97706',
      label: isDark ? '#FCD34D' : '#B45309',
    },
    indigo: {
      bg: isDark ? '#312E81' : '#EEF2FF',
      border: isDark ? '#3730A3' : '#C7D2FE',
      value: isDark ? '#818CF8' : '#4F46E5',
      label: isDark ? '#A5B4FC' : '#4338CA',
    },
    red: {
      bg: isDark ? '#7F1D1D' : '#FEF2F2',
      border: isDark ? '#991B1B' : '#FECACA',
      value: isDark ? '#F87171' : '#DC2626',
      label: isDark ? '#FCA5A5' : '#B91C1C',
    },
    gray: {
      bg: isDark ? '#1D293D' : '#F9FAFB',
      border: isDark ? '#314158' : '#E5E7EB',
      value: isDark ? '#90A1B9' : '#4B5563',
      label: isDark ? '#90A1B9' : '#6B7280',
    },
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const params: { startDate?: string; endDate?: string } = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await getUserAccounts(userId, params);
      setData(response);
    } catch (error: any) {
      toast.error(error.message || t('admin.accountsAnalysis.errors.fetchAccountsFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [userId]);

  const handleApplyFilter = () => {
    fetchData();
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center"
      style={{
        background: isDark ? 'rgba(2, 6, 24, 0.75)' : 'rgba(241, 245, 249, 0.75)',
        backdropFilter: 'blur(10px)',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Modal */}
      <div
        className="relative w-full max-w-5xl max-h-[80vh] overflow-hidden mx-4 flex flex-col"
        style={{ background: mainBg, borderRadius: '16px', boxShadow: isDark ? '0 25px 50px rgba(0,0,0,0.5)' : '0 25px 50px rgba(0,0,0,0.15)' }}
      >
        {/* Header */}
        <div
          className="px-6 py-4 flex items-center justify-between"
          style={{ background: headerBg, borderBottom: `1px solid ${cardBorder}` }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: iconContainerBg }}
            >
              <Globe size={20} style={{ color: iconColor }} />
            </div>
            <div>
              <h2 className="text-base font-semibold" style={{ color: textPrimary }}>
                {t('admin.accountsAnalysis.userAccounts') || 'User Accounts'}
              </h2>
              {data && (
                <p className="text-xs" style={{ color: subtitleColor }}>
                  {data.user.name} - {data.user.email}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors"
            style={{ color: textSecondary }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = isDark ? '#314158' : '#F1F5F9';
              e.currentTarget.style.color = textPrimary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = textSecondary;
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Filters */}
        <div
          className="px-6 py-3"
          style={{ background: headerBg, borderBottom: `1px solid ${cardBorder}` }}
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.dateRange') || 'Date Range'}:</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-2 py-1.5 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 w-[130px]"
              style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputText }}
            />
            <span className="text-xs" style={{ color: textSecondary }}>to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-2 py-1.5 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 w-[130px]"
              style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputText }}
            />
            <button
              onClick={handleApplyFilter}
              className="px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
              style={{ background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)', color: '#FFFFFF' }}
            >
              {t('common.apply') || 'Apply'}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={`p-6 overflow-y-auto flex-1 ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`}>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Spinner size="large" text={t('common.loading') || 'Loading...'} />
            </div>
          ) : !data ? (
            <div className="text-center py-20">
              <AlertCircle size={48} style={{ color: textSecondary, margin: '0 auto', marginBottom: '12px' }} />
              <p className="text-sm" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.noDataFound') || 'No data found'}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {[
                  { value: data.summary.totalAccounts, label: t('admin.accountsAnalysis.totalAccounts') || 'Total Accounts', colors: statCardColors.blue },
                  { value: data.summary.activeAccounts, label: t('admin.accountsAnalysis.activeAccounts') || 'Active', colors: statCardColors.green },
                  { value: data.summary.periodStats.totalJobs, label: t('admin.accountsAnalysis.totalJobs') || 'Total Jobs', colors: statCardColors.indigo },
                  { value: data.summary.totalAccounts - data.summary.activeAccounts, label: t('admin.accountsAnalysis.inactive') || 'Inactive', colors: statCardColors.gray },
                ].map((stat, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg p-3 text-center"
                    style={{ background: stat.colors.bg, border: `1px solid ${stat.colors.border}` }}
                  >
                    <p className="text-lg font-bold" style={{ color: stat.colors.value }}>{stat.value}</p>
                    <p className="text-xs" style={{ color: stat.colors.label }}>{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* Activity Period Stats */}
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                {[
                  { value: data.summary.periodStats.totalLikes, label: t('admin.accountsAnalysis.likes') || 'Likes', colors: statCardColors.red },
                  { value: data.summary.periodStats.totalComments, label: t('admin.accountsAnalysis.comments') || 'Comments', colors: statCardColors.blue },
                  { value: data.summary.periodStats.totalFollows, label: t('admin.accountsAnalysis.follows') || 'Follows', colors: statCardColors.green },
                  { value: data.summary.periodStats.totalMessages, label: t('admin.accountsAnalysis.messages') || 'Messages', colors: statCardColors.purple },
                  { value: data.summary.periodStats.totalStories, label: t('admin.accountsAnalysis.stories') || 'Stories', colors: statCardColors.amber },
                  { value: data.summary.periodStats.totalPosts, label: t('admin.accountsAnalysis.posts') || 'Posts', colors: statCardColors.teal },
                ].map((stat, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg p-3 text-center"
                    style={{ background: stat.colors.bg, border: `1px solid ${stat.colors.border}` }}
                  >
                    <p className="text-lg font-bold" style={{ color: stat.colors.value }}>{stat.value}</p>
                    <p className="text-xs" style={{ color: stat.colors.label }}>{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* Accounts List */}
              <div className="space-y-4">
                {data.accounts.length === 0 ? (
                  <div
                    className="text-center py-12 rounded-lg"
                    style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
                  >
                    <Inbox size={48} style={{ color: textSecondary, margin: '0 auto', marginBottom: '12px' }} />
                    <p className="text-sm" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.noAccountsConnected') || 'No accounts connected'}</p>
                  </div>
                ) : (
                  data.accounts.map((account) => (
                    <div
                      key={account._id}
                      className="rounded-lg overflow-hidden"
                      style={{ background: mainBg, border: `1px solid ${cardBorder}` }}
                    >
                      {/* Account Header */}
                      <div
                        className="px-4 py-4 flex items-start justify-between"
                        style={{ borderBottom: `1px solid ${borderColor}` }}
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className="w-14 h-14 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
                            style={{ background: isDark ? '#314158' : '#E5E7EB' }}
                          >
                            {account.profilePictureUrl ? (
                              <img src={account.profilePictureUrl} alt={account.username} className="w-full h-full object-cover" />
                            ) : (
                              <Globe size={24} style={{ color: textSecondary }} />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-semibold" style={{ color: textPrimary }}>@{account.username}</p>
                            <p className="text-xs capitalize" style={{ color: textSecondary }}>{account.platform} • {account.profile.accountType}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                style={account.isActive
                                  ? { background: isDark ? '#14532D' : '#DCFCE7', color: isDark ? '#4ADE80' : '#15803D' }
                                  : { background: isDark ? '#314158' : '#F3F4F6', color: isDark ? '#90A1B9' : '#374151' }
                                }
                              >
                                {account.isActive ? 'Active' : 'Inactive'}
                              </span>
                              {account.isLocked && (
                                <span
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                  style={{ background: isDark ? '#7F1D1D' : '#FEE2E2', color: isDark ? '#F87171' : '#B91C1C' }}
                                >
                                  <Lock size={10} style={{ marginRight: '4px' }} />
                                  Locked
                                </span>
                              )}
                              {account.proxy?.assigned && (
                                <span
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                  style={{ background: isDark ? '#1C398E' : '#DBEAFE', color: isDark ? '#51A2FF' : '#1D4ED8' }}
                                >
                                  <Globe size={10} style={{ marginRight: '4px' }} />
                                  Proxy: {account.proxy.country}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold" style={{ color: textPrimary }}>{account.profile.followersCount.toLocaleString()}</p>
                          <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.followers') || 'Followers'}</p>
                        </div>
                      </div>

                      {/* Account Stats */}
                      <div
                        className="px-4 py-3 grid grid-cols-2 md:grid-cols-3 gap-4"
                        style={{ background: cardBg }}
                      >
                        <div>
                          <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.likes') || 'Likes'}</p>
                          <p className="text-sm font-semibold" style={{ color: textPrimary }}>
                            {account.stats?.likes.periodTotal || 0}
                            <span className="text-xs font-normal ml-1" style={{ color: textSecondary }}>/ {account.stats?.likes.total || 0} total</span>
                          </p>
                        </div>
                        <div>
                          <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.comments') || 'Comments'}</p>
                          <p className="text-sm font-semibold" style={{ color: textPrimary }}>
                            {account.stats?.comments.periodTotal || 0}
                            <span className="text-xs font-normal ml-1" style={{ color: textSecondary }}>/ {account.stats?.comments.total || 0} total</span>
                          </p>
                        </div>
                        <div>
                          <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.follows') || 'Follows'}</p>
                          <p className="text-sm font-semibold" style={{ color: textPrimary }}>
                            {account.stats?.follows.periodTotal || 0}
                            <span className="text-xs font-normal ml-1" style={{ color: textSecondary }}>/ {account.stats?.follows.total || 0} total</span>
                          </p>
                        </div>
                        <div>
                          <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.messages') || 'Messages'}</p>
                          <p className="text-sm font-semibold" style={{ color: textPrimary }}>
                            {account.stats?.messages.periodTotal || 0}
                            <span className="text-xs font-normal ml-1" style={{ color: textSecondary }}>/ {account.stats?.messages.total || 0} total</span>
                          </p>
                        </div>
                        <div>
                          <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.stories') || 'Stories'}</p>
                          <p className="text-sm font-semibold" style={{ color: textPrimary }}>
                            {account.stats?.stories?.periodTotal || 0}
                            <span className="text-xs font-normal ml-1" style={{ color: textSecondary }}>/ {account.stats?.stories?.total || 0} total</span>
                          </p>
                        </div>
                        <div>
                          <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.posts') || 'Posts'}</p>
                          <p className="text-sm font-semibold" style={{ color: textPrimary }}>
                            {account.stats?.posts?.periodTotal || 0}
                            <span className="text-xs font-normal ml-1" style={{ color: textSecondary }}>/ {account.stats?.posts?.total || 0} total</span>
                          </p>
                        </div>
                      </div>

                      {/* Jobs & Rules */}
                      <div
                        className="px-4 py-3 grid grid-cols-2 md:grid-cols-5 gap-4"
                        style={{ borderTop: `1px solid ${borderColor}` }}
                      >
                        <div>
                          <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.totalJobs') || 'Total Jobs'}</p>
                          <p className="text-sm font-semibold" style={{ color: textPrimary }}>{account.jobs.total}</p>
                        </div>
                        <div>
                          <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.completed') || 'Completed'}</p>
                          <p className="text-sm font-semibold" style={{ color: isDark ? '#4ADE80' : '#16A34A' }}>{account.jobs.completed}</p>
                        </div>
                        <div>
                          <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.failed') || 'Failed'}</p>
                          <p className="text-sm font-semibold" style={{ color: isDark ? '#F87171' : '#DC2626' }}>{account.jobs.failed}</p>
                        </div>
                        <div>
                          <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.successRate') || 'Success Rate'}</p>
                          <p className="text-sm font-semibold" style={{ color: isDark ? '#51A2FF' : '#2563EB' }}>{account.jobs.successRate.toFixed(1)}%</p>
                        </div>
                        <div>
                          <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.activeRules') || 'Active Rules'}</p>
                          <p className="text-sm font-semibold" style={{ color: isDark ? '#C084FC' : '#9333EA' }}>{account.rules.active}/{account.rules.total}</p>
                        </div>
                      </div>

                      {/* Action Settings */}
                      {account.rules.settings.length > 0 && (
                        <div
                          className="px-4 py-3"
                          style={{ borderTop: `1px solid ${borderColor}` }}
                        >
                          <p className="text-xs font-semibold mb-2" style={{ color: titleColor }}>{t('admin.accountsAnalysis.actionSettings') || 'Action Settings'}</p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {account.rules.settings.map((setting) => (
                              <div
                                key={setting.actionId}
                                className="px-3 py-2 rounded text-xs"
                                style={{
                                  background: setting.isActive
                                    ? (isDark ? '#14532D' : '#F0FDF4')
                                    : (isDark ? '#1D293D' : '#F9FAFB'),
                                  border: `1px solid ${setting.isActive
                                    ? (isDark ? '#166534' : '#BBF7D0')
                                    : cardBorder}`,
                                }}
                              >
                                <p className="font-medium truncate" style={{ color: textPrimary }}>{setting.friendlyName}</p>
                                <p style={{ color: textSecondary }}>{setting.category}</p>
                                <div className="mt-1 flex items-center justify-between">
                                  <span style={{ color: textSecondary }}>{setting.limitUtilized}/{setting.limit}</span>
                                  <span
                                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium"
                                    style={setting.isActive
                                      ? { background: isDark ? '#14532D' : '#DCFCE7', color: isDark ? '#4ADE80' : '#15803D' }
                                      : { background: isDark ? '#314158' : '#F3F4F6', color: isDark ? '#90A1B9' : '#6B7280' }
                                    }
                                  >
                                    {setting.isActive ? 'ON' : 'OFF'}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Timestamps */}
                      <div
                        className="px-4 py-2 flex items-center justify-between text-xs"
                        style={{ background: cardBg, borderTop: `1px solid ${borderColor}`, color: textSecondary }}
                      >
                        <span>{t('admin.accountsAnalysis.created') || 'Created'}: {formatDate(account.createdOn)}</span>
                        <span>{t('admin.accountsAnalysis.lastJob') || 'Last Job'}: {formatDate(account.lastJobRun)}</span>
                        <span>{t('admin.accountsAnalysis.nextJob') || 'Next Job'}: {formatDate(account.nextJobTime)}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UserAccountsModal;
